import { computed, onMounted, reactive, ref } from 'vue';

import type { FormInstance } from '@flash-global66/g-form';

import {
  authService,
  countryService,
  onboardingService as sharedOnboardingService,
} from '@/shared/common';

import { isValidPhoneNumber } from 'libphonenumber-js';
import { logOut } from '@/helpers/auth';
import { logger } from '@/shared/common/utils/logger.util';

import { useOtpFlow } from '@/shared/otp';
import { toast } from '@flash-global66/g-toast';
import { useLoader } from '@flash-global66/g-loader';
import type { FormRules } from '@flash-global66/g-form';
import { useDialCodeSelector } from '@/composables/useDialCodeSelector';

import { useAccountStore } from '@/stores/account';

import { ErrorsOtp } from '@/shared/common/errors';
import { getReasonMessage } from '@/shared/common';

import type { CountryData } from '@/types/CountryType';
import type { ICustomerPhoneInformation } from '@/shared/onboardingForms/types/personalInformation.type';
import type { INextOtp} from '../../types/otp.type';
import { BusinessFlow, OtpChannelEnum } from '../../types/otp.type';
import type { UseOtpVerifyPhoneOptions } from '../../types/otpVerifyPhone.type';

type FormContact = {
  dialCodeSelected: CountryData | null;
  phoneNumber: string;
};

export const useOtpVerifyPhone = (options: UseOtpVerifyPhoneOptions) => {
  const formContact = reactive<FormContact>({
    dialCodeSelected: null as CountryData | null,
    phoneNumber: '',
  });
  const countries = ref<CountryData[]>([]);
  const formUserRef = ref<FormInstance | null>(null);

  const otpFlow = useOtpFlow();
  const storeAccount = useAccountStore();
  const { showGLoader } = useLoader();
  const { countriesOptions, onFilterCountry } = useDialCodeSelector(countries);

  const rules = computed<FormRules>(() => ({
    dialCodeSelected: [
      { required: true, message: 'Seleccione un opción', trigger: 'change' },
    ],
    phoneNumber: [
      { required: true, message: 'Este es obligatorio', trigger: 'blur' },
      {
        validator(rule, value) {
          const isValid = isValidPhoneNumber(value, {
            defaultCallingCode: formContact.dialCodeSelected?.dialCode || '',
          });

          if (!isValid) {
            return new Error('El número de teléfono no es válido');
          }
          return true;
        },
        trigger: 'blur',
      },
    ],
  }));

  const countrySelectOptions = computed(() => {
    return countriesOptions.value.map((country) => ({
      value: country,
      title: `(+${country.dialCode}) ${country.nameES}`,
      icon: country.alpha2Code,
    }));
  });

  const updateKycStatus = async (): Promise<boolean> => {
    let isSuccess = false;
    const result = await authService.getKycStatus();

    result.fold(
      (failure) => {
        const errorMessage = getReasonMessage({
          failure,
          defaultMessage: 'Error al actualizar el estado KYC.',
        });
        toast.error(errorMessage);
      },
      (success) => {
        const { data } = success;

        storeAccount.setKyc(data);
        isSuccess = true;
      }
    );

    return isSuccess;
  };

  const updatePhoneNumber = async (userPhone: ICustomerPhoneInformation) => {
    const result = await sharedOnboardingService.updateCustomerPhone(userPhone);

    return result.fold(
      (failure) => {
        const errorMessage = getReasonMessage({
          failure,
          defaultMessage: 'Error al actualizar el teléfono',
        });
        toast.error(errorMessage);
        return false;
      },
      () => true
    );
  };

  const validatePhone = async (otp: string, type: string) => {
    let isSuccess = false;
    const result = await sharedOnboardingService.postOnboardingValidationPhone({
      authType: type,
      authValue: otp,
    });

    result.fold(
      (failure) => {
        const errorMessage = getReasonMessage({
          failure,
          errorMap: ErrorsOtp,
          defaultMessage: 'Error al validar el teléfono',
        });
        otpFlow.setOtpError(errorMessage);
      },
      () => {
        isSuccess = true;
      }
    );

    return isSuccess;
  };

  async function performPhoneUpdate(formContact: FormContact) {
    showGLoader(true);

    try {
      const userPhone = {
        callingCode: formContact.dialCodeSelected?.dialCode || '',
        phone: formContact.phoneNumber,
      };

      const phoneUpdated = await updatePhoneNumber(userPhone);
      if (!phoneUpdated) return false;

      return true;
    } finally {
      showGLoader(false);
    }
  }

  const handleOtpValidation = async (
    { otp, type }: INextOtp,
    otpFlow: ReturnType<typeof useOtpFlow>
  ) => {
    showGLoader(true);

    try {
      const isValid = await validatePhone(otp, type);
      if (!isValid) return;

      const kycUpdated = await updateKycStatus();
      if (!kycUpdated) return;

      toast.success('Teléfono validado correctamente');
      otpFlow.hideOtp();
      options.onNavigateToMovements();
    } finally {
      showGLoader(false);
    }
  };

  async function onSendOtp() {
    if (!formUserRef.value) {
      toast.error('Formulario no disponible');
      return;
    }
    try {
      await formUserRef.value.validate();
    } catch (validationError) {
      logger.error(JSON.stringify(validationError));
      return;
    }

    const updateSuccess = await performPhoneUpdate(formContact);
    if (updateSuccess) {
      verifyPhoneNumber();
    }
  }

  const verifyPhoneNumber = () => {
    otpFlow.setFlowConfig({
      channel: OtpChannelEnum.WHATSAPP,
      inputsToHide: [OtpChannelEnum.EMAIL],
      businessFlow: BusinessFlow.VERIFY_PHONE_B2B,
    });

    otpFlow.setOnNext((params: INextOtp) =>
      handleOtpValidation(params, otpFlow)
    );

    otpFlow.showOtp();
  };

  async function setCountries() {
    const result = await countryService.getCountries();

    result.fold(
      (failure) => {
        const errorMessage = getReasonMessage({
          failure,
          defaultMessage: 'Ocurrió un error al intentar traer los países',
        });
        toast.error(errorMessage);
      },
      ({ data: countriesData }) => {
        countries.value = countriesData;
      }
    );
  }

  onMounted(() => {
    setCountries();
  });

  return {
    rules,
    formContact,
    countrySelectOptions,
    formUserRef,
    onFilterCountry,
    onSendOtp,
    logOut,
  };
};
